<template>
    <div class="collection" ref="collection">
        <div class="collection-header collection-header-sticky" :class="{ 'collection-header-sticky-stuck': headerStuck }" ref="header">
            <div class="d-flex align-items-center justify-content-md-between justify-content-center flex-wrap gx-2 gy-1">
                <div style="flex-grow: 1">
                    <slot name="header">&nbsp;</slot>
                </div>
                <div v-if="searchable" class="d-lg-none">
                    <ButtonView type="shaded" color="foreground" size="small" :class="{ active: mergedParams.search && mergedParams.search.length > 0 }" @click="showingSearchOptions = true">
                        <i class="fa-regular fa-magnifying-glass fa-fw"></i>
                    </ButtonView>
                </div>
                <CollectionSearchView class="d-none d-lg-unset my-n1" size="small" style="max-width: 140px; min-width: 80px" v-if="searchable" :value="mergedParams.search ?? ''" @update:value="searchUpdated($event)"/>
                <ResponsivePopoverView v-model:active="showingOrderOptions" size="small" placement="bottom">
                    <template v-slot:target>
                        <ButtonView v-if="orderable && orderable.length > 0" type="shaded" color="foreground" size="small" :class="{ active: order.field && order.direction }" @click="showingOrderOptions = true">
                            <i class="fa-regular fa-arrow-up-arrow-down fa-fw"></i>
                        </ButtonView>
                    </template>
                    <template v-slot:header>
                        <div class="d-flex align-items-center justify-content-between g-4">
                            <i class="fa-duotone fa-arrow-up-arrow-down fa-fw" style="font-size: 1.4em"></i>
                            <ModalCloseButtonView @click="showingOrderOptions = false"/>
                        </div>
                    </template>
                    <template v-slot:body>
                        <CollectionOrderOptionsView :orderable="orderable" :selected="order.field" :direction="order.direction" @update:selected="order.field = $event" @update:direction="order.direction = $event"/>
                    </template>
                </ResponsivePopoverView>
                <div v-if="response.total > 0">
                    <ResponsivePopoverView v-model:active="showingPaginationOptions" size="small" placement="bottom">
                        <template v-slot:target>
                            <ButtonView type="shaded" color="foreground" size="small" :style="response.total <= 10 ? { 'pointer-events': 'none' } : {}" @click="response.total > 10 ? showingPaginationOptions = true : undefined">
                <span class="d-flex align-items-center" style="font-size: 0.85em">
                  {{ (pageNumber - 1) * mergedParams.limit + 1 }} <span class="text-muted mx-1">-</span> {{ ((pageNumber - 1) * mergedParams.limit) + response.data.length }} <span class="text-muted mx-2">/</span> {{ response.total }}
                </span>
                            </ButtonView>
                        </template>
                        <template v-slot:header>
                            <div>
                                {{ (pageNumber - 1) * mergedParams.limit + 1 }} <span class="text-muted mx-1">-</span> {{ ((pageNumber - 1) * mergedParams.limit) + response.data.length }} <span class="text-muted mx-2">/</span> {{ response.total }}
                            </div>
                        </template>
                        <template v-slot:body>
                            <CollectionPaginationOptionsView :limit="mergedParams.limit" :page="pageNumber" :total="response.total" @update:limit="updateQuery(q => { q.limit = $event }); showingPaginationOptions = false;" @update:page="updateQuery(q => { q.page = $event }); showingPaginationOptions = false;"/>
                        </template>
                    </ResponsivePopoverView>
                </div>
                <div v-if="response.total > mergedParams.limit" class="d-flex align-items-center">
                    <ButtonView type="shaded" color="foreground" size="small" :disabled="pageNumber <= 1" @click="updateQuery(q => { q.page = 1 })">
                        <i class="fas fa-angles-left fa-fw"></i>
                    </ButtonView>
                    <ButtonView type="shaded" color="foreground" size="small" :disabled="pageNumber <= 1" @click="updateQuery(q => { q.page = pageNumber - 1 })">
                        <i class="fas fa-angle-left fa-fw"></i>
                    </ButtonView>
                    <ButtonView type="shaded" color="foreground" size="small" :disabled="pageNumber >= totalPages" @click="updateQuery(q => { q.page = pageNumber + 1 })">
                        <i class="fas fa-angle-right fa-fw"></i>
                    </ButtonView>
                    <ButtonView type="shaded" color="foreground" size="small" :disabled="pageNumber >= totalPages" @click="updateQuery(q => { q.page = totalPages })">
                        <i class="fas fa-angles-right fa-fw"></i>
                    </ButtonView>
                </div>
            </div>
        </div>
        <div class="collection-body">
            <HttpContentView :endpoint="endpoint" :reload="reload" @update:reload="$emit('update:reload', $event)" :params="mergedParams" @update:loading="loading = $event" @response="response = $event">
                <template v-slot:content>
                    <div v-if="response.data.length === 0">
                        <div class="text-center" style="margin-top: 12%; margin-bottom: 12%">
                            <i class="fa-duotone fa-folder-open text-primary" style="font-size: 6em; opacity: 0.1"></i>
                        </div>
                    </div>
                    <div v-else>
                        <slot name="content" :items="response.data" :loading="loading"></slot>
                    </div>
                </template>
            </HttpContentView>
        </div>
        <ModalView v-model:active="showingSearchOptions" size="small">
            <template v-slot:header>
                <div class="d-flex align-items-center justify-content-between g-4">
                    <i class="fa-duotone fa-magnifying-glass fa-fw" style="font-size: 1.4em"></i>
                    <ModalCloseButtonView @click="showingSearchOptions = false"/>
                </div>
            </template>
            <template v-slot:body>
                <CollectionSearchView :value="mergedParams.search ?? ''" @update:value="searchUpdated($event)"/>
            </template>
        </ModalView>
    </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import HttpContentView from "../http/HttpContentView.vue";
import ModalView from "../../plugins/modal/ModalView.vue";
import CollectionPaginationOptionsView from "./CollectionPaginationOptionsView.vue";
import CollectionOrderOptionsView from "../collections/CollectionOrderOptionsView.vue";
import CollectionSearchView from "../collections/CollectionSearchView.vue";
import ResponsivePopoverView from "../general/ResponsivePopoverView.vue";
import ButtonView from "../general/ButtonView.vue";
import ModalCloseButtonView from "../../plugins/modal/ModalCloseButtonView.vue";

const props = defineProps({
  endpoint: String,
  params: Object,
  defaultOrder: String,
  searchable: Boolean,
  reload: Boolean,
  orderable: {
    type: Array,
    validator: value => {
      return value.find(item => typeof item !== "object" || !item.key || !item.name || typeof item.key !== "string" || typeof item.name !== "string") === undefined
    }
  }
});

const emit = defineEmits(["update:reload", "update:total", "update:items"]);

const collection = ref(null);

const showingSearchOptions = ref(false);
const showingPaginationOptions = ref(false);
const showingOrderOptions = ref(false);

const loading = ref(false);
const order = ref({
  direction: "asc",
  field: undefined
});
const response = ref({
  data: [],
  total: 0
});

const lastParams = ref(undefined);

const header = ref(null);

const route = useRoute();
const router = useRouter();

const query = computed(() => route.query);

const headerStuck = ref(false);

function scrolled() {
  const rect = header.value?.getBoundingClientRect();
  headerStuck.value = rect && rect.y === 0;
}

function updateQuery(callable) {
  let value = {...query.value };
  callable(value);
  router.replace({ query: value });
}

function searchUpdated(search) {
  if (query.value.search !== search) {
    if (search && search.length > 0) {
      updateQuery(q => {
        q.search = search;
        q.page = 1;
      });
    } else {
      updateQuery(q => {
        delete q.search;
        q.page = 1;
      });
    }
  }
}

function getScrollParent(node) {
  if (node == null || !(node instanceof Element)) return null;
  let overflowY = window.getComputedStyle(node).overflowY;
  if (overflowY === "scroll") return node;
  return getScrollParent(node.parentNode);
}

let scrollParent = undefined;

onMounted(() => {
  if (props.defaultOrder) {
    updateQuery(query => query.order = props.defaultOrder);
  }
  scrollParent = getScrollParent(collection.value);
  scrollParent?.addEventListener("scroll", scrolled);
});

onUnmounted(() => {
  scrollParent?.removeEventListener("scroll", scrolled);
});

const mergedParams = computed(() => {
  const limit = query.value.limit ? parseInt(query.value.limit) : 20;
  let params = {
    search: query.value.search && query.value.search.length > 0 ? query.value.search : undefined,
    offset: query.value.page ? (parseInt(query.value.page) - 1) * limit : 0,
    limit: limit,
    order: query.value.order ? query.value.order : props.defaultOrder,
  };
  if (props.params) Object.assign(params, props.params);
  return params;
});

const pageNumber = computed(() => query.value.page ? parseInt(query.value.page) : 1);
const totalPages = computed(() => Math.ceil(response.value.total / mergedParams.value.limit));

watch(() => props.params, value => {
  if (value) {
    let stringified = JSON.stringify(value);
    if (stringified !== lastParams.value) {
      lastParams.value = stringified;
      if (query.value.page !== "1") {
        updateQuery(q => q.page = 1);
      }
    }
  }
}, {
  deep: true
});

watch(() => response.value.total, value => {
  emit("update:total", value);
});

watch(response, value => {
  emit("update:items", value.data);
});

watch(order, value => {
  if (value.direction && value.field) {
    updateQuery(q => { q.order = value.field + ":" + value.direction  });
  } else {
    updateQuery(q => { q.order = undefined  });
  }
}, {
  deep: true
});

watch(() => query.value.order, value => {
  if (value) {
    const split = value.split(":");
    if (split.length === 2 && ["asc", "desc"].includes(split[1])) {
      order.value.field = split[0];
      order.value.direction = split[1];
      return;
    }
  }
  order.value.field = undefined;
}, {
  immediate: true
});

</script>